import React from 'react';
import classNames from 'classnames/bind';
import parseHtmlToReact, { domToReact } from 'html-react-parser';
import update from 'immutability-helper';
import PropTypes from 'prop-types';
import { captureException } from '@sentry/react';

import { SCRIPT_NODE_TYPE, SCRIPT_TAG, TAG_NODE_TYPE } from './constants';
import { replaceInsertionsByTable } from './helpers';
import styles from './styles.pcss';

const cx = classNames.bind(styles);

/**
 * A component handler for custom html scripts
 * given scriptData, component will evaluate it after component is mounted
 */
class CustomScriptExecutor extends React.PureComponent {
  componentDidMount() {
    const { attribs, scriptData } = this.props;
    const scriptTag = document.getElementById('includeContentScript');
    try {
      if ((attribs || []).length !== 0) {
        Object.keys(attribs).forEach((key) => {
          if (scriptTag) {
            /** set attribute may cause an issue when applying empty named attributes */
            try {
              if (key !== 'type') {
                scriptTag.setAttribute(key, attribs[key]);
              }
            } catch (e) {
              /** delay error throw to prevent crash */
              setTimeout(() => {
                throw e;
              });
            }
          }
        });
      }
      try {
        eval(scriptData); // eslint-disable-line no-eval
      } catch (e) {
        captureException(e);
      }
    } catch (e) {
      /** delay error throwing, to prevent react crash */
      setTimeout(() => {
        throw e;
      }, 1);
    }
  }

  render() {
    const { attribs, scriptData } = this.props;
    const type = attribs.type || 'text/javascript';
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: `<script type=${type} id="includeContentScript">${scriptData}</script>`,
        }}
      />
    );
  }
}

CustomScriptExecutor.propTypes = {
  attribs: PropTypes.any,
  scriptData: PropTypes.string,
};

class CustomAttributeApplier extends React.Component {
  componentDidMount() {
    return this.applyCustomAttributes();
  }

  componentDidUpdate() {
    return this.applyCustomAttributes();
  }

  applyCustomAttributes = () => {
    const { customAttributes } = this.props;
    Object.keys(customAttributes).forEach((key) => {
      if (this.domNode) {
        /** set attribute may cause an issue when applying empty named attributes */
        try {
          this.domNode.setAttribute(key, customAttributes[key]);
        } catch (e) {
          /** delay error throw to prevent crash */
          setTimeout(() => {
            throw e;
          });
        }
      }
    });
  };

  render() {
    const { children } = this.props;
    return React.cloneElement(children, {
      ref: (elem) => {
        this.domNode = elem;
      },
    });
  }
}

CustomAttributeApplier.propTypes = {
  children: PropTypes.node,
  customAttributes: PropTypes.object,
};

export class IncludeContent extends React.Component {
  /** extract the script tag content from the component */
  getScriptNodeContents = (node) => node.children.length && node.children[0].data;

  getReplaceTagsDict = () =>
    Object.keys(this.props.replaceTags).reduce(
      (result, key) =>
        update(result, {
          [key.toLowerCase()]: { $set: this.props.replaceTags[key] },
        }),
      {},
    );

  /** replace function definition for html-react-parser, only replaces given tags */
  replaceNode = (node, index) => {
    const replaceTags = this.getReplaceTagsDict();

    if (node.type === SCRIPT_NODE_TYPE && node.name === SCRIPT_TAG) {
      /** if its script tag, we replace it via CustomScriptExecutor, to eval content on mount */
      return (
        <CustomScriptExecutor
          attribs={node.attribs}
          key={+index}
          scriptData={this.getScriptNodeContents(node)}
        />
      );
    }
    if (node.type === TAG_NODE_TYPE) {
      // eslint-disable-next-line no-restricted-syntax
      for (const attributeName in node.attribs) {
        if (node.removeAttribute) node.removeAttribute(attributeName);
      }

      const ReplaceTag = replaceTags[node.name];
      if (ReplaceTag) {
        return <ReplaceTag key={+index}>{domToReact(node.children, this.options)}</ReplaceTag>;
      }

      return undefined;
    }
    /** otherwise the default html-react-parser behavior is executed */
    return undefined;
  };

  options = {
    replace: this.replaceNode,
    // eslint-disable-next-line react/no-unstable-nested-components
    transform(reactNode, domNode) {
      /**
       * if node is a tag and it has at least one custom attribute,
       * we wrap it with CustomAttributeApplier to force apply non-react attribute,
       * like style, onclick in form of string, etc
       */
      if (domNode?.attribs && Object.keys(domNode.attribs).length) {
        return (
          <CustomAttributeApplier customAttributes={domNode.attribs}>
            {reactNode}
          </CustomAttributeApplier>
        );
      }

      return reactNode;
    },
  };

  deleteLineBreaks = (string) => string.replace(/\n/g, '');

  /**
   * build reference link from template,
   * replacing variable place holders with values
   * and special tags with react elements
   */
  renderHtml() {
    const { htmlContent, replaceTable } = this.props;

    if (!htmlContent) return htmlContent;
    if (typeof htmlContent !== 'string') return '';
    /**
     * TODO: remove this hardcode when the issue gets fixed on backend
     * its meant to fix backend issue with inability to safely output script tags
     */
    const unescapedHtmlContent = htmlContent.replace(/<(\/|)escaped_script/g, '<$1script');
    const escapedHtmlContent = this.deleteLineBreaks(unescapedHtmlContent);

    return parseHtmlToReact(
      replaceInsertionsByTable(escapedHtmlContent, replaceTable) || '',
      this.options,
    );
  }

  render() {
    const { className, noStyle, onClick, stylesBind } = this.props;
    const children = this.renderHtml();
    return (
      children && (
        <div
          className={stylesBind({ component: !noStyle }, className)}
          onClick={onClick}
          role="button"
          tabIndex={-1}
        >
          {children}
        </div>
      )
    );
  }
}

IncludeContent.propTypes = {
  className: PropTypes.string,
  htmlContent: PropTypes.string,
  noStyle: PropTypes.bool,
  onClick: PropTypes.func,
  replaceTable: PropTypes.objectOf(PropTypes.string), // key: value pairs for replacement
  replaceTags: PropTypes.objectOf(PropTypes.func),
  stylesBind: PropTypes.func,
};

IncludeContent.defaultProps = {
  htmlContent: '',
  onClick: () => {},
  replaceTable: {},
  replaceTags: {},
  stylesBind: cx,
};
